import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Plus, ImageIcon, Video } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useManuals } from '../../contexts/ManualsContext';

export const MediaModal = () => {
    const { darkMode } = useTheme();
    const { showMediaModal, setShowMediaModal, editorContent, setEditorContent } = useManuals();
    const [mediaType, setMediaType] = useState('image');
    const [mediaUrl, setMediaUrl] = useState('');
    const [mediaAlt, setMediaAlt] = useState('');
    const [mediaCaption, setMediaCaption] = useState('');

    if (!showMediaModal) return null;

    const handleInsert = () => {
        if (!mediaUrl.trim()) {
            alert('Пожалуйста, введите URL');
            return;
        }
        let mediaHtml = '';
        const captionHtml = mediaCaption.trim() ? `<p class="text-sm text-gray-500 mt-1 dark:text-gray-400">${mediaCaption}</p>` : '';
        if (mediaType === 'image') {
            mediaHtml = `
<div class="my-4">
    <img src="${mediaUrl}" alt="${mediaAlt || 'Изображение'}" class="rounded-lg max-w-full h-auto" />
    ${captionHtml}
</div>`;
        } else {
            mediaHtml = `
<div class="my-4">
    <video controls class="w-full rounded-lg">
        <source src="${mediaUrl}" type="video/mp4">
        Ваш браузер не поддерживает воспроизведение видео.
    </video>
    ${captionHtml}
</div>`;
        }
        setEditorContent(editorContent + mediaHtml);
        setShowMediaModal(false);
        setMediaUrl('');
        setMediaAlt('');
        setMediaCaption('');
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowMediaModal(false)}
        >
            <div
                className={`rounded-xl shadow-2xl w-full max-w-md p-6 ${
                    darkMode ? 'bg-gray-800' : 'bg-white'
                }`}
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-4">
                    <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Добавить медиа</h2>
                    <button
                        onClick={() => setShowMediaModal(false)}
                        className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Тип медиа</label>
                        <div className="flex space-x-4">
                            <button
                                onClick={() => setMediaType('image')}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                                    mediaType === 'image'
                                        ? (darkMode ? 'bg-blue-700 text-blue-200' : 'bg-blue-100 text-blue-700')
                                        : (darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100')
                                }`}
                            >
                                <ImageIcon className="w-4 h-4" />
                                <span>Изображение</span>
                            </button>
                            <button
                                onClick={() => setMediaType('video')}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                                    mediaType === 'video'
                                        ? (darkMode ? 'bg-blue-700 text-blue-200' : 'bg-blue-100 text-blue-700')
                                        : (darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100')
                                }`}
                            >
                                <Video className="w-4 h-4" />
                                <span>Видео</span>
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {mediaType === 'image' ? 'URL изображения' : 'URL видео'}
                        </label>
                        <input
                            type="url"
                            value={mediaUrl}
                            onChange={(e) => setMediaUrl(e.target.value)}
                            className={`w-full px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                            }`}
                            placeholder={mediaType === 'image' ? 'https://example.com/image.jpg' : 'https://example.com/video.mp4'}
                        />
                    </div>
                    {mediaType === 'image' && (
                        <>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Описание (alt)</label>
                                <input
                                    type="text"
                                    value={mediaAlt}
                                    onChange={(e) => setMediaAlt(e.target.value)}
                                    className={`w-full px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                                    }`}
                                    placeholder="Описание изображения"
                                />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Подпись</label>
                                <input
                                    type="text"
                                    value={mediaCaption}
                                    onChange={(e) => setMediaCaption(e.target.value)}
                                    className={`w-full px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                                    }`}
                                    placeholder="Подпись к изображению (необязательно)"
                                />
                            </div>
                        </>
                    )}
                    {mediaType === 'video' && (
                        <div>
                            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Подпись</label>
                            <input
                                type="text"
                                value={mediaCaption}
                                onChange={(e) => setMediaCaption(e.target.value)}
                                className={`w-full px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border border-gray-300'
                                }`}
                                placeholder="Подпись к видео (необязательно)"
                            />
                        </div>
                    )}
                    <div className="flex justify-end space-x-3 pt-2">
                        <button
                            onClick={() => setShowMediaModal(false)}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                                darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            Отмена
                        </button>
                        <button
                            onClick={handleInsert}
                            className={`px-4 py-2 rounded-lg transition-colors flex items-center ${
                                darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
                            }`}
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Добавить
                        </button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};